/**
 * UnmanagedNotice — the honest banner for tracker-live elements the BANK has
 * no row for (another TrafficPulse environment — e.g. production — rotated
 * this flow and minted new tracker ids). Rules can't see or rotate them,
 * which otherwise reads as a silent "No changes".
 *
 * Counts are grouped BY LAYER (item pools are named after their owning path,
 * so raw pool names read like paths — meaningless here; the per-pool split
 * lives in the hover detail). One "Sync bank now" button always covers every
 * affected layer in one go, and "Keep synced automatically" arms the standing
 * Auto-pull so the bank follows the other environment without a human. When
 * auto-pull already covers everything, the banner says so instead.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { CloudOff, RefreshCw, Timer } from 'lucide-react';
import { ALL_LAYERS, type FlowLayer, type QueuePoolSummary } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { useBankSync } from './useBankSync';

const LAYER_NOUN: Record<FlowLayer, [string, string]> = {
  paths: ['path', 'paths'],
  landers: ['lander', 'landers'],
  offers: ['offer', 'offers'],
};

const noun = (layer: FlowLayer, n: number) => LAYER_NOUN[layer][n === 1 ? 0 : 1];

export interface UnmanagedNoticeProps {
  pools: QueuePoolSummary[];
  provider: QueueProvider;
  campaignId: string;
}

export const UnmanagedNotice: FC<UnmanagedNoticeProps> = ({
  pools,
  provider,
  campaignId,
}) => {
  const sync = useBankSync(provider, campaignId);
  const affected = pools.filter((p) => (p.unmanaged ?? 0) > 0);
  const count = affected.reduce((n, p) => n + (p.unmanaged ?? 0), 0);
  if (count === 0) return null;

  const byLayer = ALL_LAYERS.map((layer) => ({
    layer,
    n: affected
      .filter((p) => p.layer === layer)
      .reduce((s, p) => s + (p.unmanaged ?? 0), 0),
  })).filter((e) => e.n > 0);
  const layers = byLayer.map((e) => e.layer);
  const summary = byLayer.map((e) => `${e.n} ${noun(e.layer, e.n)}`).join(' · ');
  // Hover detail keeps the per-pool split, labelled by layer so "PiperSpin"
  // reads as "landers on PiperSpin", not as a path.
  const detail = byLayer
    .map((e) =>
      affected
        .filter((p) => p.layer === e.layer)
        .map((p) => `${noun(e.layer, 2)} · ${p.poolName}: ${p.unmanaged}`)
        .join(' · '),
    )
    .join(' | ');
  const needsArming = sync.unarmed(layers);

  return (
    <div
      className="flex flex-wrap items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[11px] text-foreground"
      title={detail}
    >
      <CloudOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="font-semibold">
          {summary} on the tracker {count === 1 ? 'is' : 'are'} not connected to
          the bank yet
        </span>{' '}
        (missing row or missing link) — rules can’t see or rotate them, so they
        never show a replacement.
        Another TrafficPulse server (e.g. production) keeps rotating this flow
        on {provider}, so new tracker elements appear over time.{' '}
        {needsArming.length === 0 ? (
          <span className="font-medium">
            Auto-pull is on — this clears on its next sync.
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-6 gap-1 border-warning/50 text-[11px]"
          loading={sync.syncing !== null}
          disabled={sync.syncing !== null}
          onClick={() => void sync.syncNow(layers)}
        >
          <RefreshCw className="h-3 w-3" aria-hidden />
          {sync.syncing ? `Syncing ${sync.syncing}…` : 'Sync bank now'}
        </Button>
        {needsArming.length > 0 ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-6 gap-1 text-[11px]"
            loading={sync.arming}
            disabled={sync.arming}
            title={`Arms the Banks page Auto-pull for ${needsArming.join(' + ')} — the bank then follows the tracker on a schedule, no clicks needed.`}
            onClick={() => void sync.keepSynced(needsArming)}
          >
            <Timer className="h-3 w-3" aria-hidden />
            Keep synced automatically
          </Button>
        ) : null}
      </span>
    </div>
  );
};

export default UnmanagedNotice;
